import { useState, useEffect } from 'react';

const extractCartItemInfo = (item, url) => {
  return {
    ProductID: item?.id,
    SKU: item?.articleNumber,
    ProductName: item?.product?.name,
    Quantity: item?.quantity,
    ItemPrice: item?.previousUnitPrice?.incVat,
    RowTotal: item?.total?.incVat,
    ProductURL: `${url}/${item?.product?.primaryRoute?.id}`,
    ImageURL: item?.product?.images?.[0]?.url
  };
};

export const KlaviyoTrackViewedItem = ({
  productId,
  product,
  selectedChannel
}) => {
  let url = selectedChannel?.url;
  useEffect(() => {
    if (typeof window !== 'undefined' && product) {
      if (!window._learnq) window._learnq = [];

      const productCategories = (product.categories || []).map(
        category => category.name
      );

      const item = {
        ProductName: `${product.name}`,
        ProductID: `${product.id}`,
        SKU: `${product.articleNumber}`,
        Categories: productCategories,
        ImageURL: `${product.images?.[0]?.url}`,
        URL: `${url}/${product.primaryRoute?.id}`,
        Brand: '',
        Price: product.price.incVat
      };

      window._learnq.push(['track', 'Viewed Product', item]);

      window._learnq.push([
        'trackViewedItem',
        {
          Title: item.ProductName,
          ItemId: item.ProductID,
          Categories: item.Categories,
          ImageUrl: item.ImageURL,
          Url: item.URL,
          Metadata: {
            Brand: item.Brand,
            Price: item.Price
          }
        }
      ]);
    }
  }, [productId, product, url]);
  return null;
};

export const KlaviyoTrackAddToCartItems = ({ cart, selectedChannel }) => {
  const [previousCartItems, setPreviousCartItems] = useState();
  const findAddedProduct = newCartItems => {
    let increasedQuantity;

    const addedProduct = newCartItems.find(newItem => {
      const previousItem = previousCartItems.find(
        _prevItem => _prevItem.id === newItem.id
      );
      if (previousItem == null) {
        return true;
      } else {
        if (previousItem.quantity < newItem.quantity) {
          increasedQuantity = newItem.quantity - previousItem.quantity;
          return true;
        }
      }
      return false;
    });

    return [addedProduct, increasedQuantity];
  };

  const reportAddedToCart = (cart, addedItem, increasedQuantity) => {
    if (typeof window !== 'undefined' && cart?.items?.length) {
      if (!window._learnq) window._learnq = [];
      let url = selectedChannel?.url;

      const itemNames = cart.items.map(item => item.product?.name);
      const cartItems = cart.items.map(product =>
        extractCartItemInfo(product, url)
      );

      const payload = {
        $value: cart.productTotal.incVat,
        AddedItemProductName: addedItem.product?.name,
        AddedItemProductID: addedItem.id,
        AddedItemSKU: addedItem.articleNumber,
        AddedItemImageURL: addedItem.product?.images?.[0]?.url,
        AddedItemURL: `${url}/${addedItem.product?.primaryRoute?.id}`,
        AddedItemPrice: addedItem.previousUnitPrice?.incVat,
        AddedItemQuantity: increasedQuantity || addedItem.quantity,
        ItemNames: itemNames,
        CheckoutURL: cart.externalCheckoutUrl,
        Items: cartItems
      };

      console.log('Klaviyo cart info !', payload);

      window._learnq.push(['track', 'Added to Cart', payload]);
    }
  };

  useEffect(() => {
    let addedItem, increasedQuantity;
    if (previousCartItems != null && cart?.items)
      [addedItem, increasedQuantity] = findAddedProduct(cart.items);

    setPreviousCartItems(cart?.items);

    if (addedItem) reportAddedToCart(cart, addedItem, increasedQuantity);
  }, [cart]);

  return null;
};

export const klaviyoTrackStartedCheckout = (cart, selectedChannel) => {
  if (typeof window !== 'undefined') {
    if (!window._learnq) window._learnq = [];
    let url = selectedChannel?.url;
    const timestamp = new Date().getTime();

    const itemNames = cart.items.map(item => item.product?.name);
    const cartItems = cart.items.map(product =>
      extractCartItemInfo(product, url)
    );

    console.log(cartItems);

    const startedCheckout = {
      $event_id: `${cart.id}_${timestamp}`,
      $value: cart.productTotal.incVat,
      ItemNames: itemNames,
      CheckoutURL: cart.externalCheckoutUrl,
      Items: cartItems
    };

    window._learnq.push(['track', 'Started Checkout', startedCheckout]);
  }

  return;
};

// Klaviyo public company_id per domain. A domain listed here is what turns
// Klaviyo on for that shop: the onsite script, the tracking and the
// back-in-stock subscription form on out of stock products.
export const KLAVIYO_COMPANY_IDS = {
  'alpina.no': 'Xnqn8p',
  'brusletto.no': 'SWwVyP',
  'brusletto.se': 'VjX7uT',
  'crispi.no': 'T9GcjC',
  'camelbak.no': 'VkMRwb',
  'camelbak.se': 'QVZx4c',
  'getvital.no': 'XzFgPN',
  'giroshop.no': 'TRhVqU',
  'hallmarkofsweden.se': 'SAu9W5',
  'hallmarkofsweden.no': 'RiM4zd',
  'leathermanshop.se': 'THweBY',
  'leathermanshop.no': 'Wdmb53',
  'ledlensershop.no': 'WKhd9P',
  'ledlenser.se': 'WA9ssv',
  'markabutikken.no': 'Y6riJQ',
  'nordicgrip.no': 'VVPpHb',
  'nordicgrip.se': 'RnX4D8',
  'stanley.no': 'Y4DNzf',
  'stanley1913.se': 'Xa5YLQ'
};

function channelDomain(selectedChannel) {
  return selectedChannel?.url?.replace('https://', '').replace('www.', '');
}

export function getKlaviyoCompanyId(selectedChannel) {
  return KLAVIYO_COMPANY_IDS[channelDomain(selectedChannel)];
}

// Klaviyo list per domain, used only by the newsletter opt-in on the
// back-in-stock form. A back-in-stock mail counts as marketing, so a profile
// that never consented, or unsubscribed earlier, is skipped by the flow.
// Ticking the box subscribes them to this list and lifts that suppression.
// Channels missing here simply get no checkbox.
export const KLAVIYO_LIST_IDS = {
  'alpina.no': 'WzYMQE',
  'brusletto.no': 'TXCkTa',
  'brusletto.se': 'TcxhEt',
  'camelbak.no': 'WFk4dS',
  'camelbak.se': 'Uj6CjZ',
  'crispi.no': 'TpkisS',
  'getvital.no': 'SJEfGD',
  'hallmarkofsweden.no': 'XYt9En',
  'hallmarkofsweden.se': 'TiAy5v',
  'leathermanshop.no': 'TwA4WC',
  'leathermanshop.se': 'Y49pTY',
  'ledlensershop.no': 'XqT7fU',
  'markabutikken.no': 'T7Uxe3',
  'nordicgrip.no': 'Ww63s4',
  'nordicgrip.se': 'XCDn7A',
  'stanley1913.se': 'VpDsKY'
};

export function getKlaviyoListId(selectedChannel) {
  return KLAVIYO_LIST_IDS[channelDomain(selectedChannel)];
}

const KlaviyoProvider = ({ selectedChannel }) => {
  const apiKey = getKlaviyoCompanyId(selectedChannel);

  useEffect(() => {
    if (!apiKey) {
      console.log(
        `No channel in KlaviyoProvider for the selected domain. Looking for: ${selectedChannel?.url}`
      );
      console.log(selectedChannel);
      return;
    }

    const script = document.createElement('script');
    script.type = 'application/javascript';
    script.async = true;
    script.defer = true;
    script.src = `https://static.klaviyo.com/onsite/js/klaviyo.js?company_id=${apiKey}`;

    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, [apiKey, selectedChannel]);
  return null;
};

export default KlaviyoProvider;
